import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { _HttpClient, SettingsService } from '@delon/theme';
import { NzSelectComponent, NzSelectModeType } from 'ng-zorro-antd/select';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';

import { CustomerAddConfigService } from '../../inline-add-panel/customer-add-config.service';
import { SelectBaseComponent } from '../select-base';
import { SelectInstantBaseNewComponent } from '../selectInstant-base-new';

@Component({
  selector: 'app-agent-select',
  templateUrl: './agent-select.component.html',
  styles: [
    `
      .agent-select-container {
        position: relative;
        width: 100%;
      }

      .add-new-button {
        width: 100%;
        border: 1px dashed #d9d9d9;
        background: #fafafa;
        color: #1890ff;
        font-weight: 500;
        transition: all 0.2s;
      }

      .add-new-button:hover {
        border-color: #1890ff;
        background: #f0f8ff;
      }

      ::ng-deep
        .dropdownSelect:not(:has(.ant-select-item-option-active))
        .ant-select-item-option:nth-child(2):not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
      ::ng-deep
        .dropdownSelect
        .ant-select-item-option:first-child:last-child:not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AgentSelectComponent),
      multi: true
    }
  ]
})
export class AgentSelectComponent
  extends SelectInstantBaseNewComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() activeOnly = true;
  @Input() mode: NzSelectModeType = 'default';
  @Input() defaultType?: 'agent' = 'agent';
  @Input() tabindex: string | null = null;
  @Input() skipRestrictions = false;
  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;

  NotShowInReport: boolean = false;
  destroy$ = new Subject<void>();

  // Inline add panel properties
  agentConfig: any;
  inlineDefaults: any = {};
  // @HostListener('focusin') onFocusIn() {
  //   this.focusInput();
  // }
  constructor(
    http: _HttpClient,
    router: Router,
    cdr: ChangeDetectorRef,
    private resueTabService: ReuseTabService,
    public settingsService: SettingsService,
    private customerAddConfigService: CustomerAddConfigService,
    private elementRef: ElementRef
  ) {
    super(http, cdr, router);
  }
  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.checkAddNewOptionInReportAccess();

    // Initialize agent config
    this.agentConfig = this.customerAddConfigService.getAgentConfig();

    // Subscribe to search form changes to store instant data
    this.searchForm.controls.keyword.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          const trimmedValue = value.trim();
          this.storedInstantData = trimmedValue;
          // Also store in pendingInstantData for Add New (handled by base class)
          this.pendingInstantData = trimmedValue;
        }
      });

    // Subscribe to list filled event for item activation
    this.onListFilled.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.ensureFirstItemActivatedAndFocused();
    });
  }

  ngAfterViewInit(): void {
    // Set tabindex on the search input
    if (this.tabindex) {
      setTimeout(() => {
        const searchInput = this.elementRef?.nativeElement?.querySelector(
          '.ant-select-selection-search-input'
        );
        if (searchInput) {
          searchInput.setAttribute('tabindex', this.tabindex);
        }
      }, 100);
    }

    // Listen to input events to capture search value
    setTimeout(() => {
      const searchInput = this.elementRef?.nativeElement?.querySelector(
        '.ant-select-selection-search-input'
      );
      if (searchInput) {
        searchInput.addEventListener('input', () => {
          if (searchInput.value) {
            const trimmedValue = searchInput.value.trim();
            this.storedInstantData = trimmedValue;
            // Also store in pendingInstantData for Add New (handled by base class)
            this.pendingInstantData = trimmedValue;
          }
        });
      }
    }, 100);
  }

  getUrl(searchValue: string): string {
    if (searchValue) {
      // Check if this is a valueField match request (indicated by special prefix)
      const isValueFieldMatch = searchValue.startsWith(
        SelectBaseComponent.VALUEFIELD_MATCH_PREFIX
      );
      const actualSearchValue = isValueFieldMatch
        ? searchValue.substring(
            SelectBaseComponent.VALUEFIELD_MATCH_PREFIX.length
          )
        : searchValue;

      // Ensure searchValue is a string before calling replaceAll
      const searchStr = String(actualSearchValue).replaceAll("'", "''");

      // Check if searchValue can be converted to int (ID)
      const searchValueNum = parseInt(actualSearchValue, 10);
      const isNumericId =
        !isNaN(searchValueNum) &&
        searchValueNum.toString() === String(actualSearchValue).trim();

      if (isValueFieldMatch) {
        // ValueField match: use eq operator for precise matching based on valueField
        // Ignore prerequisite conditions (like isActive) when matching by valueField
        // This ensures we can find records even if they are inactive
        let filter: string;
        if (this.valueField === 'id') {
          // Match by ID exactly
          filter = `id eq ${isNumericId ? searchValueNum : actualSearchValue}`;
        } else {
          // Match by the specified valueField exactly (code, name, etc.)
          // Use the valueField if set, otherwise default to 'code'
          const fieldName = this.valueField || 'code';
          filter = `${fieldName} eq '${encodeURIComponent(searchStr)}'`;
        }
        let url = `api/agents/lookup?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
        if (this.skipRestrictions) {
          url += `&skipRestrictions=true`;
        }
        return url;
      } else {
        // Partial match: use contains for search behavior with prerequisite conditions
        let filter = `isActive eq true`;
        if (!this.activeOnly) {
          filter += ` or isActive eq false`;
        }
        if (isNumericId) {
          // Include ID matching: (Id eq <id>) or (code/name contains search)
          filter = `(${filter}) and ((id eq ${searchValueNum}) or (contains(code, '${encodeURIComponent(
            searchStr
          )}') or contains(name,'${encodeURIComponent(searchStr)}')))`;
        } else {
          // Original behavior: only search by code/name
          filter = `(${filter}) and (contains(code, '${encodeURIComponent(
            searchStr
          )}') or contains(name,'${encodeURIComponent(searchStr)}'))`;
        }
        let url = `api/agents/lookup?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
        if (this.skipRestrictions) {
          url += `&skipRestrictions=true`;
        }
        return url;
      }
    } else {
      // No search value: apply prerequisite conditions
      let filter = `isActive eq true`;
      if (!this.activeOnly) {
        filter += ` or isActive eq false`;
      }
      let url = `api/agents/lookup?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
      if (this.skipRestrictions) {
        url += `&skipRestrictions=true`;
      }
      return url;
    }
  }

  // set the location want to navigate
  jump(): string {
    return '/ar/agent/detail/0';
  }

  // set the param
  queryParam(): String {
    return 'AgentName';
  }

  checkAddNewOptionInReportAccess(): boolean {
    let curUrl = this.resueTabService.curUrl.toLowerCase();

    if (curUrl.includes('report') || curUrl.includes('einv/')) {
      this.NotShowInReport = true;
    } else {
      this.NotShowInReport = false;
    }

    return this.NotShowInReport;
  }

  // Override search method to store instant data
  override search(keyword: string, delay: number = 100): void {
    // Don't search if inline add panel is open
    if (this.showInlineAddPanel) {
      return;
    }
    // Base class handles storing in both storedInstantData and pendingInstantData if keyword is not empty
    super.search(keyword, delay);
  }

  // // Override setKeyword method to store instant data
  // override setKeyword(keyword: string): void {
  //   super.setKeyword(keyword);
  //   this.storedInstantData = keyword || '';
  // }

  // Override the addNew method to show inline panel instead of navigation
  override addNew(): void {
    // Use pendingInstantData which was captured synchronously in base class onAddNewClick
    // This ensures we have the data even if storedInstantData was cleared
    const instantDataToUse = this.pendingInstantData || this.storedInstantData;

    // If we still don't have data, try to capture it
    if (!instantDataToUse) {
      this.captureCurrentSearchValue();
      const currentSearchValue = this.getCurrentSearchValue();
      if (currentSearchValue) {
        this.pendingInstantData = currentSearchValue;
      }
    }

    // Final fallback - use whatever we have
    const finalInstantData =
      this.pendingInstantData || this.storedInstantData || '';

    // Load options and defaults first, then show panel
    Promise.all([this.loadSelectOptions(), this.fetchInlineDefaults()]).then(
      () => {
        // Merge instant data into initialData as fallback
        if (finalInstantData) {
          this.inlineDefaults = {
            ...this.inlineDefaults,
            code: finalInstantData
          };
        }

        this.showInlineAddPanel = true;
        // Force change detection to ensure the panel is rendered
        this.cdr.detectChanges();

        // Retry mechanism to ensure changeInstantData is called after form is ready
        const trySetInstantData = (retries: number = 10) => {
          if (!finalInstantData) return;

          if (this.inlineAddPanel && this.inlineAddPanel.formGroup) {
            const instantFieldName =
              this.agentConfig?.fields?.find((f: any) => f.isInstantData)
                ?.name || 'code';
            const control = this.inlineAddPanel.formGroup.get(instantFieldName);
            if (control) {
              this.inlineAddPanel.changeInstantData(finalInstantData);
              return;
            }
          }

          if (retries > 0) {
            setTimeout(() => trySetInstantData(retries - 1), 50);
          }
        };

        // Try immediately and with retries
        setTimeout(() => {
          if (this.inlineAddPanel && finalInstantData) {
            this.inlineAddPanel.changeInstantData(finalInstantData);
          }
          trySetInstantData();
        }, 0);

        if (this.nzSelectComponent) {
          this.nzSelectComponent.setOpenState(false);
        }
      }
    );
  }
  onAgentCreated(event: { item: any; selectionConfig?: any }): void {
    if (event.item && event.selectionConfig) {
      // Create the agent object in the expected format
      const agent = {
        id: event.item[event.selectionConfig.valueField],
        code: event.item.code,
        name: event.item.name,
        isActive: event.item.isActive || false,
        isDefault: event.item.isDefault || false
      };

      // Add the new agent to the list and select it
      this.list.unshift(agent);

      // Close the inline panel first
      this.showInlineAddPanel = false;

      // Ensure the item is present in the list
      this.ensureItemPresentList(agent);

      // Set the agent object in the form control to trigger agent.valueChanges
      this.value = agent.id;
      this.selected = agent;
      this.valueChanged(agent.id);

      // Set the agent object directly in the form control
      this.onChange(agent);
      this.writeValue(agent);

      // Clear stored instant data after successful creation
      this.clearStoredInstantData();

      // Re-enable the select component
      if (this.nzSelectComponent) {
        this.nzSelectComponent.nzDisabled = false;
      }

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  onAgentCancelled(): void {
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
    // Re-enable the select component
    if (this.nzSelectComponent) {
      this.nzSelectComponent.nzDisabled = false;
    }
  }

  onAgentMoreDetails(): void {
    // Navigate to agent detail page with pre-filled data
    const queryParams: any = {
      autoPopulate: '1'
    };

    // Get current form data from the inline panel
    if (this.inlineAddPanel && this.inlineAddPanel.formGroup) {
      const formData = this.inlineAddPanel.formGroup.value;

      if (formData.code) queryParams.AgentCode = formData.code;
      if (formData.name) queryParams.AgentName = formData.name;
      if (formData.isActive !== undefined)
        queryParams.IsActive = formData.isActive;
      if (formData.isDefault !== undefined)
        queryParams.IsDefault = formData.isDefault;
    }

    this.router.navigate(['/ar/agent/detail/0'], {
      queryParams: queryParams
    });
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onFieldChange(event: { fieldName: string; value: any }): void {
    // Handle field changes if needed
  }

  onPanelVisibleChange(visible: boolean): void {
    this.showInlineAddPanel = visible;
    if (!visible) {
      // Re-enable the select component when panel is closed
      if (this.nzSelectComponent) {
        this.nzSelectComponent.nzDisabled = false;
      }
    }
  }

  private async loadSelectOptions(): Promise<void> {
    // No additional options needed for agents
    return Promise.resolve();
  }

  private async fetchInlineDefaults(): Promise<void> {
    try {
      const response = await firstValueFrom(this.http.get('api/agents/new'));
      this.inlineDefaults = response || {};
    } catch (error) {
      console.error('Error fetching agent defaults:', error);
      this.inlineDefaults = {};
    }
  }

  // private applyDefaultActiveOption(delayMs: number = 0): void {
  //   setTimeout(() => {
  //     try {
  //       const dropdowns = document.querySelectorAll('.dropdownSelect');
  //       const dropdown = dropdowns[dropdowns.length - 1] as HTMLElement | undefined;
  //       if (!dropdown) return;
  //       const options = dropdown.querySelectorAll('.ant-select-item-option');
  //       if (!options || options.length === 0) return;

  //       // Determine target index:
  //       // - If more than one option exists, highlight the first real result (index 1)
  //       // - Otherwise highlight the first option (index 0)
  //       let targetIndex = 0;
  //       if (!this.NotShowInReport && options.length > 1) {
  //         targetIndex = 1; // First real result (skip Add New button)
  //       }

  //       // Use a more robust approach: simulate mouse events to properly manage state
  //       this.setActiveOptionByIndex(options, targetIndex);
  //     } catch {}
  //   }, delayMs);
  // }

  // private setActiveOptionByIndex(options: NodeListOf<Element>, targetIndex: number): void {
  //   if (options[targetIndex]) {
  //     // Remove active class from all options
  //     options.forEach(option => option.classList.remove('ant-select-item-option-active'));

  //     // Add active class to target option
  //     options[targetIndex].classList.add('ant-select-item-option-active');

  //     // Simulate mouse enter event to properly manage state
  //     const mouseEnterEvent = new MouseEvent('mouseenter', {
  //       bubbles: true,
  //       cancelable: true,
  //       view: window
  //     });
  //     options[targetIndex].dispatchEvent(mouseEnterEvent);
  //   }
  // }
  override async writeValue(value: any) {
    // Extract ID if value is an object (e.g., from API response)
    let idValue = value;
    if (value && typeof value === 'object' && this.valueField) {
      idValue = value[this.valueField] || value.id;
    }

    this.value = idValue;

    // If use selected item as value
    if (this.value) {
      if (!this.valueField) {
        this.ensureItemPresentList(value);
        this.selected = value;
      } else {
        // When valueField is set, we need to fetch the list to find the matching item
        // Use valueField match to find the record that exactly matches the valueField (ID or Code)
        // Prefix keyword with special marker to indicate valueField match (ignores prerequisite conditions)
        let keyword = `${SelectBaseComponent.VALUEFIELD_MATCH_PREFIX}${idValue}`;
        await this.fetchData(keyword);
        this.selected = this.list.find(x => x[this.valueField] == idValue);
      }
    } else {
      this.selected = null;
      //this.list = [];
    }
  }
}
